<template>
  <div class="companies">
    <SectionHeader
      :title="$t('containerGroups.containerGroups')"
      :projectSelect="true"
      @selectProjectUuids="
        projectUuids = $event
        getContainerGroups()
      "
    />
    <div class="body-content">
      <CompanyNav />
      <div class="body">
        <div class="container-groups-filter">
          <button
            @click="
              showOnlyDisabled = !showOnlyDisabled
              getContainerGroups()
            "
            class="button button-grey button-small button-round"
            :class="{ disabled: !includesDisabledGroups }"
            :disabled="!includesDisabledGroups"
          >
            {{ showOnlyDisabled ? $t('containerGroups.showAll') : $t('containerGroups.showOnlyDisabled') }}
          </button>
        </div>
        <SearchField
          v-if="!showLoadingSpinner"
          :label="$t('meta.searchForLabel', { entity: $t('meta.containerGroup') })"
          :name="'container-group-search-input'"
          :placeholder="$t('containerGroups.searchPlaceholder')"
          v-model="searchPhrase"
          @input="searchHandler"
        />
        <LoadingSpinner v-if="loading && showLoadingSpinner" />
        <template v-else-if="containerGroups && containerGroups.length == 0">
          <p class="alert alert-danger">
            {{ $t('containerGroups.noGroups') }}
          </p>
        </template>
        <template v-else>
          <ListItem
            :has-details="true"
            :action-to-link="'edit'"
            :title-link="`/company/container_groups/${group.uuid}`"
            :title="group.name"
            v-for="group in containerGroups"
            :key="group.uuid"
            :class="[group.status]"
            :opensOnLeft="false"
            :actions="[
              {
                iconName: group.status == 'enabled' ? 'visibility' : 'visibility_off',
                clickEvent: () => toggleGroupStatus(group.uuid),
                tooltip:
                  group.status == 'enabled' ? $t('containerGroups.disableGroup') : $t('containerGroups.enableGroup'),
              },
            ]"
          >
            <template v-slot:row-cols>
              <div class="main-row__col">
                {{ group.container_count }}
                {{ $t('containers.containers') }}
              </div>
            </template>
            <template v-slot:toggle-area>
              <div class="group-wrapper" v-if="'containers' in group">
                <div class="group__header">
                  <span class="group__details--with-icon">
                    <span class="material-icons">gps_fixed</span>
                    {{ group.preformatted || $t('containerGroups.noAddress') }}</span
                  >
                  <span class="group__details--with-icon">
                    <span class="material-icons">all_inbox</span>
                    {{ group.project.name }}
                  </span>
                </div>
                <div class="group__body" v-if="'containers' in group">
                  <Table>
                    <template #header>
                      <th>
                        {{ $t('containerGroups.name') }}
                      </th>
                      <th>{{ $t('containerGroups.fraction') }}</th>
                      <th>
                        {{ $t('containerGroups.emptyTemplates') }}
                      </th>
                      <th>{{ $t('containerGroups.lastEmptying') }}</th>
                    </template>
                    <template #body>
                      <tr v-for="container in group.containers" :key="container.uuid">
                        <td>{{ container.name }}</td>
                        <td>{{ container.fraction }}</td>
                        <td>
                          <span v-for="empty_template in container.empty_templates" :key="empty_template.uuid">
                            <EmptyingTemplateDescription
                              :repeatType="empty_template.repeat_type"
                              :repeatInterval="empty_template.repeat_interval"
                              :referenceDate="empty_template.reference_date"
                            />
                          </span>
                        </td>
                        <td>
                          {{ container.measured_at ? formattedFromDateTime(container.measured_at) : '-' }}
                        </td>
                      </tr>
                    </template>
                  </Table>
                </div>
              </div>
            </template>
          </ListItem>
          <pagination
            v-if="pagination"
            :records="pagination.totalObjects"
            v-model="pagination.page"
            :per-page="pagination.perPage"
            @paginate="getContainerGroups"
            :options="{ chunk: 5, texts: { count: $t('pagination.text') } }"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import Pagination from 'vue-pagination-2'

import EmptyingTemplateDescription from '@/components/EmptyingTemplates/Description.vue'
import SearchField from '@/components/Form/Search/SearchField'
import ListItem from '@/components/ListItems/index.vue'
import LoadingSpinner from '@/components/LoadingSpinner'
import SectionHeader from '@/components/SectionHeader'
import Table from '@/components/Table'
import CompanyNav from '@/components/company_nav'
import dateUtils from '@/components/mixins/dateUtils'

import containerGroupsApi from '@/api/containerGroups'
import { SEARCH_INPUT_DELAY } from '@/utils/searchInputDelay'

export default {
  name: 'company_container_groups',
  props: {
    searchInputDelay: {
      type: Number,
      default: SEARCH_INPUT_DELAY,
    },
  },
  inject: ['companyUser'],
  data: () => ({
    containerGroups: [],
    loading: true,
    pagination: { perPage: 25, page: 1, totalObjects: 0 },
    projectUuids: [],
    searchPhrase: '',
    showLoadingSpinner: false,
    showOnlyDisabled: false,
  }),
  mixins: [dateUtils],
  components: {
    CompanyNav,
    ListItem,
    EmptyingTemplateDescription,
    Pagination,
    LoadingSpinner,
    SearchField,
    SectionHeader,
    Table,
  },
  computed: {
    searchHandler() {
      return debounce(
        () => {
          // The resulting records of the search cause a new pagination
          // and we start with page 1 for showing the results.
          this.getContainerGroups(1, false)
        },
        this.searchInputDelay, // Limit search API calls to once per 400ms while typing
        { trailing: true },
      )
    },
    includesDisabledGroups() {
      return this.containerGroups.some(group => group.status === 'disabled')
    },
  },
  methods: {
    async getContainerGroups(page = 1, showLoadingSpinner = true) {
      page = page || this.pagination.page
      this.loading = true
      this.showLoadingSpinner = showLoadingSpinner
      const params = {
        company_uuid: this.companyUser.company_uuid,
        detail: true,
        status: this.showOnlyDisabled ? 'disabled' : null,
        page,
        per_page: this.pagination.perPage,
        project_uuids: this.projectUuids,
      }

      if (this.searchPhrase.length > 0) {
        params['search_phrase'] = this.searchPhrase
      }

      const result = await containerGroupsApi.getAll(params)

      this.containerGroups = result.containerGroups
      this.pagination.page = page
      this.pagination.totalObjects = result.pagination?.total_objects
      this.loading = false
      this.showLoadingSpinner = false
    },
    getContainerGroup(groupUuid) {
      return this.containerGroups.find(group => group.uuid === groupUuid)
    },
    async toggleGroupStatus(groupUuid) {
      const group = this.getContainerGroup(groupUuid)

      const result = await containerGroupsApi.update(groupUuid, {
        status: group.status == 'enabled' ? 'disabled' : 'enabled',
      })

      if (result.success) group.status = result.containerGroup.status
    },
  },
  mounted() {
    this.getContainerGroups(1)
  },
}
</script>

<style scoped lang="scss">
.disabled {
  opacity: 0.5;
}
.container-groups-filter {
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  margin: 20px 0 10px;
}

.group-wrapper {
  padding: 1rem 0;

  .group__header {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .group__details--with-icon {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .material-icons {
      margin-left: 0;
    }
  }
}
</style>
